import { Component, effect, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import Handlebars from 'handlebars';

import { DataSourceService } from '@app/finxone-web-frontend/app/lib/services/data-source-service/data-source.service';
import {
  dispatchCompaniesHouseSelectedOfficer,
  dispatchPrimaryOfficer,
  mapOfficerToFormFields,
} from '@app/finxone-web-frontend/app/lib/utils/companies-house-officer.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  CompaniesHouseOfficer,
  DropdownMenuDirection,
  FormFieldConfig,
  LookupType,
  LookupsCommonType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, takeUntil, tap } from 'rxjs';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';

@Component({
  standalone: false,
  selector: 'app-dropdown-input-field',
  templateUrl: './dropdown-input-field.component.html',
})
export class DropdownInputFieldComponent implements OnInit {
  @AutoDestroy private destroy$ = new Subject<void>();

  @Input() formGroup!: FormGroup;
  @Input() inputFieldConfig!: FormFieldConfig;
  @Input() validationConfig: any[] = [];
  @Input() formControlInvalid = false;
  @Input() renderedHelperTextContent = '';
  @Input() inputLabelTooltipText = '';

  public options: LookupsCommonType[] = [];
  private readonly optionsLoaded = signal(false);
  public dropdownOptionItemTemplateFunction: (ctx: LookupsCommonType) => string = () => '';
  public isLibraryConfig = false;
  public isPreselectDisabled = false;
  public defaultVisibleItemsCount = 5;
  public defaultMenuDirection = DropdownMenuDirection.AUTO;

  // Derived control name for metadata — avoids collisions with real fields
  private get metadataControlName(): string {
    return `${this.inputFieldConfig.name}_metadata`;
  }

  private dataSourceService = inject(DataSourceService);
  private readonly workflowFormsService = inject(WorkflowFormsService);
  private readonly store = inject(Store);

  constructor() {
    effect(() => {
      const isProgressDataAssigned = this.workflowFormsService.formProgressDataAssigned();
      const optionsLoaded = this.optionsLoaded();
      // Only validate once options are available, otherwise a correct value
      // would be wrongly cleared because options is still empty.
      if (isProgressDataAssigned && optionsLoaded) {
        this.checkControlValueAndRemoveIfNotInOptions();
      }
    });
  }

  ngOnInit(): void {
    if (this.inputFieldConfig) {
      this.ensureMetadataControl();
      this.setupDropdownConfig();
      this.watchSelectionForMetadata();
    }
  }

  private ensureMetadataControl() {
    if (!this.formGroup.contains(this.metadataControlName)) {
      this.formGroup.addControl(this.metadataControlName, new FormControl(null));
    }
  }

  private watchSelectionForMetadata() {
    const control = this.formGroup.controls[this.inputFieldConfig.name];
    if (!control) return;

    control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncMetadataControl();
      this.autofillCompanyHouseOfficerFields();
    });
  }

  private syncMetadataControl() {
    const metadata = this.selectedItem?.metadata ?? null;
    this.formGroup.controls[this.metadataControlName]?.setValue(metadata, { emitEvent: false });
  }

  private autofillCompanyHouseOfficerFields(): void {
    if (this.inputFieldConfig?.['dataDropdownOption'] !== LookupType.COMPANIES_HOUSE_DIRECTORS) return;
    const officer = (this.selectedItem?.metadata ?? {}) as CompaniesHouseOfficer;

    // Exclude the dropdown's own control: patching it back would re-emit its
    // valueChanges (Angular emits on patchValue regardless of whether the
    // value changed) and re-enter this handler, causing infinite recursion.
    const fieldsToPatch = mapOfficerToFormFields(officer ?? null);
    delete fieldsToPatch[this.inputFieldConfig.name];

    this.formGroup.patchValue(fieldsToPatch, { emitEvent: true });
    dispatchCompaniesHouseSelectedOfficer(this.store, officer ?? null);
    dispatchPrimaryOfficer(this.store, officer ?? null);
  }

  private setupDropdownConfig() {
    this.isLibraryConfig = this.inputFieldConfig?.['configureAnswerActiveTab'] === 'Library';
    this.isPreselectDisabled = !!this.inputFieldConfig?.isPreselectDisabled;

    // If preselect is disabled, clear the dropdown's value to avoid showing a preselected option
    if (this.isPreselectDisabled) {
      const dropdownControl = this.formGroup.controls[this.inputFieldConfig.name];
      dropdownControl?.setValue('');
    }

    if (this.isLibraryConfig) {
      const dataDropdownOption = this.inputFieldConfig?.['dataDropdownOption'] as LookupType;

      // load options from datasource
      this.dataSourceService
        .getDataSourceByType(dataDropdownOption)
        .pipe(
          takeUntil(this.destroy$),
          tap((response) => {
            this.options = response;
            this.optionsLoaded.set(true);
            this.syncMetadataControl();
          }),
        )
        .subscribe();

      // compile handlebars template
      this.dropdownOptionItemTemplateFunction = Handlebars.compile(
        this.inputFieldConfig?.['dropdownOptionItemTemplate'] ?? '',
      );
    } else {
      this.options = this.inputFieldConfig?.options ?? [];
      this.optionsLoaded.set(true);
      this.syncMetadataControl();
    }
  }

  private checkControlValueAndRemoveIfNotInOptions() {
    const value = this.formGroup.controls[this.inputFieldConfig.name]?.value;
    const optionExists = this.options.some((opt) => opt?.['value'] === value);
    if (!optionExists) {
      this.formGroup.controls[this.inputFieldConfig.name]?.setValue('');
      this.formGroup.controls[this.metadataControlName]?.setValue(null, { emitEvent: false });
    }
  }

  public get selectedItem() {
    const value = this.formGroup.controls[this.inputFieldConfig.name]?.value;
    return this.options.find((opt) => opt?.['value'] === value);
  }

  public renderSelected(): string {
    if (this.dropdownOptionItemTemplateFunction && this.selectedItem) {
      return this.dropdownOptionItemTemplateFunction(this.selectedItem);
    }
    return `<div>${this.inputFieldConfig?.inputPlaceholder ?? ''}</div>`;
  }
}
